import { MouseEventHandler, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import { ThemedProperties } from '@r1-frontend/ui-kit/themes/types';
import { useKeyPress } from '@r1-frontend/shared/hooks/useKeyPress';
import { useRootElement } from '@r1-frontend/shared/hooks/useRootElement';
import { useScrollLock } from '@r1-frontend/shared/hooks/useScrollLock';

export type TOverlayProps = {
    /**
     * Флаг отображения
     */
    isOpen: boolean,

    /**
     * Коллбек, вызываемый при закрытии
     */
    onClose: () => void,

    /**
     * Время анимации в миллисекундах
     * @default 300
     */
    animationDuration?: number,

    children?: ReactNode,
    className?: string,
}

const Overlay = styled(
    ({ isOpen, onClose, ...props }: TOverlayProps): JSX.Element | null => {
        const ref = useRef<HTMLDivElement>(null);
        const root = useRootElement({ id: 'overlay-portal' });
        const { lockScroll, unlockScroll } = useScrollLock();

        const [isShown, setIsShown] = useState(false);

        useEffect(() => {
            isOpen ? lockScroll() : unlockScroll();
            return unlockScroll;
        }, [isOpen]);

        useEffect(() => {
            (async() => {
                /**
                 * We use the nested isShown state to render the component and to control the animation.
                 * This is done so that we don't have to worry about synchronizing the animations of nested
                 * Overlay components and so that they always fire when using the onClose callback.
                 */
                if (isOpen) {
                    return setIsShown(true);
                }

                if (!isOpen && ref.current) {
                    await Promise.allSettled(
                        ref.current.getAnimations().map(animation => animation.finished),
                    );
                    setIsShown(false);
                }
            })();
        }, [isOpen]);

        const closeOverlayOnClickOutside: MouseEventHandler<HTMLDivElement> = useCallback(
            (event) => event.target === ref.current && onClose(),
            [onClose],
        );

        useKeyPress('Escape', onClose);

        const overlay = useMemo(
            () => isShown
                ? <div
                    {...props}
                    ref={ref}
                    onClick={closeOverlayOnClickOutside}
                    onMouseDown={e => e.stopPropagation()}
                >
                    {props.children}
                </div>
                : null,
            [closeOverlayOnClickOutside, isShown, props],
        );

        return root ? createPortal(overlay, root) : null;
    },
)`
    ${({ theme, animationDuration = 300, isOpen }: ThemedProperties<TOverlayProps>) => css`
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 1024;
    
        @keyframes reveal {
            from {
                opacity: 0;
            }
        }
    
        animation: reveal ${animationDuration / 1000}s ease-in;
        background-color: ${theme.colors.overlay};
        
        opacity: 0;
        transition: opacity ${animationDuration / 1000}s ease-out;
        
        ${isOpen && `
            opacity: 1;
        `}
    `}
`;

Overlay.displayName = 'Overlay';

export { Overlay };
